import React, { ReactNode, ComponentProps, useState } from 'react';
import {
    FormControl,
    FormElement,
    FormHeader,
    FormHelperText,
    FormLabel,
    Input,
    InputGroup,
    InputSuffix,
} from '@fiverr-private/forms';
import { EyeIcon, EyeSlashIcon } from '@fiverr-private/visuals';
import { Typography } from '@fiverr-private/typography';
import { IconButton } from '@fiverr-private/button';
import { translate } from '../../utils/translate';

interface PasswordInputProps
    extends Pick<ComponentProps<typeof FormControl>, 'name'>,
        Pick<ComponentProps<typeof Input>, 'id' | 'autoComplete' | 'autoFocus' | 'maxLength'>,
        Pick<ComponentProps<typeof FormHelperText>, 'hint'> {
    label?: ReactNode;
    labelColor?: ComponentProps<typeof Typography>['color'];
    hideError?: boolean;
    onVisibilityChange?: (isVisible: boolean) => void;
}

export const PasswordInput: React.FC<PasswordInputProps> = ({
    id,
    name,
    label,
    labelColor = 'grey_1100',
    autoComplete,
    autoFocus,
    maxLength,
    hideError,
    hint,
    onVisibilityChange,
}) => {
    const [isVisible, setIsVisible] = useState(false);

    const Icon = isVisible ? EyeIcon : EyeSlashIcon;

    const handleSetShowPasswordValue = () => {
        setIsVisible((prevIsVisible) => {
            const isVisible = !prevIsVisible;
            onVisibilityChange?.(isVisible);
            return isVisible;
        });
    };

    return (
        <FormControl name={name}>
            {label && (
                <FormHeader>
                    <FormLabel htmlFor={id}>
                        <Typography fontWeight="semibold" color={labelColor}>
                            {label}
                        </Typography>
                    </FormLabel>
                </FormHeader>
            )}
            <FormElement>
                <InputGroup>
                    <Input
                        id={id}
                        autoComplete={autoComplete}
                        autoFocus={autoFocus}
                        type={isVisible ? 'text' : 'password'}
                        maxLength={maxLength}
                    />
                    <InputSuffix>
                        <IconButton
                            size="xs"
                            type="button"
                            variant="ghost"
                            aria-label={translate(
                                isVisible ? 'fields.password.icon_hide' : 'fields.password.icon_show'
                            )}
                            onClick={handleSetShowPasswordValue}
                            tabIndex={-1}
                        >
                            <Icon color="grey_1100" />
                        </IconButton>
                    </InputSuffix>
                </InputGroup>
            </FormElement>
            {!hideError && <FormHelperText hint={hint} />}
        </FormControl>
    );
};
